import PropTypes from 'prop-types';
import React, {
    useState,
    useEffect
} from 'react';

import {
    Meta,
    Footer,
    FilterButton
} from '$partials/global/index.js';
import { GridWrapper } from '$partials/grid/layout/index.js';
import { NavigationPageWrapper, NavigationButton } from '$partials/navigation/index.js';
import GridModuleDispatcher from '$partials/grid/dispatcher.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */

const ArchivePage = (props) => {
    const page = props.data.page;

    // Get array of objects containing Years and total occurences
    const years = page.grid.map(item => item.year);

    const yearCount = years.reduce((acc, year) => {
        acc[year] = (acc[year] || 0) + 1;
        return acc;
    }, {});

    const yearsArray = Object.keys(yearCount).map(year => {
        return { title: parseInt(year), total: yearCount[year] };
    });

    yearsArray.sort((a, b) => b?.title - a?.title);

    // State
    const [appliedFilters, setAppliedFilters] = useState([]);

    const [itemsToShow, setItemsToShow] = useState(page?.grid);

    // Update grid items based on selected filters
    useEffect(() => {
        if (appliedFilters?.length === 0) {
            setItemsToShow(page?.grid);
        } else {
            let filteredItems = [];
            filteredItems = page?.grid?.filter(item =>
                appliedFilters?.some(filter => filter === item.year)
            );
            setItemsToShow(filteredItems);
        }
    }, [appliedFilters]);

    /**
     * Handle filter change
     * @param {string} filteredYear
     */
    const handleFilter = ((filteredYear) => {
        const isSelected = appliedFilters?.some(currentFilters => currentFilters === filteredYear);

        if (isSelected) {
            const newFilters = appliedFilters?.filter(appliedFilter => appliedFilter !== filteredYear);
            setAppliedFilters(newFilters);
        } else {
            setAppliedFilters([...appliedFilters, filteredYear]);
        }
    });

    return (
        <>
            <Meta page={page} />

            <NavigationPageWrapper {...props.data?.navigation}>
                <main id='main-content'>
                    <div className='p-1'>
                        <div className='rounded mb-[1px] layout-container pt-22 pb-28 sm:py-40 md:pt-37 md:pb-43 lg:pt-45 lg:pb-55 bg-foreground'>
                            <header className='px-layout-item type-style-2'>
                                <div className='rounded flex flex-col gap-32 sm:gap-60 md:gap-80'>
                                    <a
                                        className='flex flex-col'
                                        href={'/'}>
                                        <span>Bureau for</span>
                                        <span>Visual Affairs</span>
                                    </a>

                                    <h1 className='type-style-hero'>
                                        {page?.title}
                                    </h1>

                                    <NavigationButton />
                                </div>

                                {yearsArray?.length > 0 && (
                                    <div className='mt-15 sm:mt-22 md:mt-30 lg:mt-27 -mx-layout-item width-full overflow-x-auto flex gap-10 hide-scrollbar snap-x'>
                                        {yearsArray?.map((year, i) => {
                                            const isSelected = appliedFilters.some(currentFilters => currentFilters === year?.title);
                                            return (
                                                <div
                                                    className='snap-start flex first:pl-layout-item last:pr-layout-item'
                                                    key={`filterKey-${i}`}>
                                                    <FilterButton
                                                        filterColour={page?.filterColour}
                                                        handleFilter={handleFilter}
                                                        isSelected={isSelected}
                                                        slug={year?.title}
                                                        title={year?.title}
                                                        total={year?.total}>
                                                    </FilterButton>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </header>
                        </div>

                        {/* Outer grid */}
                        <GridWrapper>
                            {itemsToShow?.map((item) => {
                                if (!item) { return null; }

                                return (
                                    <GridModuleDispatcher
                                        {...item}
                                        key={item.id} />
                                );
                            })}
                        </GridWrapper>

                        <Footer
                            mailchimpId={props?.data?.mailchimpId?.mailchimpId}
                            {...props?.data?.footer} />
                    </div>
                </main>
            </NavigationPageWrapper>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        mailchimpId: PropTypes.object,
    })
};

ArchivePage.propTypes = propTypes;

FilterButton.propTypes = {
    appliedFilters: PropTypes.array,
    filter: PropTypes.object,
    setAppliedFilters: PropTypes.func,
};

FilterButton.propTypes = propTypes;

export default ArchivePage;
